import DashboardLayoutV3 from "layouts/layout-v3/DashboardLayout";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ProtectedRoute } from "routes/protectedRoutes";
import { ProtectedDashboard } from "routes/protectedDashboard";
import { ProtectedMySelf } from "routes/protectedMySelf";
import { validateTokenChangePassword } from "./features/auth/authAPI";
import LoaderProgress from "components/loaderProgress/LoaderProgress";
import { ConsultPrivileges } from "config/configRoutes";
import RedirectComponent from "routes/RedirectComponent";
const Loadable = (Component) => (props) => {
  return (
    <Suspense fallback={<LoaderProgress />}>
      <Component {...props} />
    </Suspense>
  );
}; // dashboards

const MainDashboard = Loadable(
  lazy(() => import("./pages/dashboard/dashboard"))
);

const MySelfDashboardIssuance = Loadable(
  lazy(() => import("./pages/dashboard-mySelf/dashboardIssuance"))
);

const MySelfDashboardReview = Loadable(
  lazy(() => import("./pages/dashboard-mySelf/dashboardReview"))
);

const ClientSection = Loadable(
  lazy(() => import("./pages/clientSection/clientDashboard"))
);
const VerificationList = Loadable(
  lazy(() => import("./pages/verificationSection/verificationDashboard"))
);
const ElectronicList = Loadable(
  lazy(() => import("./pages/dashboardElectronics/MainDashboard"))
);
const RequestManagement = Loadable(
  lazy(() => import("./pages/requestManagement/MainDashboard"))
);
const RoleList = Loadable(
  lazy(() => import("./pages/dashboardRoles/MainDashboard"))
);
const UserList = Loadable(
  lazy(() => import("./pages/dashboardsUsers/MainDashboard"))
);
const CreateUsers = Loadable(
  lazy(() => import("./pages/dashboardsUsers/Users"))
);
const EditUsers = Loadable(lazy(() => import("./pages/dashboardsUsers/Users")));
const CreateRoles = Loadable(
  lazy(() => import("./pages/dashboardRoles/Roles"))
);
const EditRoles = Loadable(lazy(() => import("./pages/dashboardRoles/Roles")));
const Ubications = Loadable(
  lazy(() => import("./pages/dashboardsLocations/MainDashboard"))
);
const ConfigurationsList = Loadable(
  lazy(() => import("./pages/dashboardConfig/MainDashboardConfigurations"))
);
const Credentials = Loadable(
  lazy(() => import("./pages/dashboardCredentials/MainDashboardCredentials"))
);
const Logistics = Loadable(
  lazy(() => import("./pages/dashboardsLogistic/MainDashboard"))
);
const Agreements = Loadable(
  lazy(() => import("./pages/dashboardsAgreeements/MainDashboard"))
);
const VerificationValidation = Loadable(
  lazy(() => import("./pages/verificationAction/verificationSection"))
);
const DocumentaryValidation = Loadable(
  lazy(() => import("./pages/documentaryValidation/documentaryValidation"))
);
const DocumentExtra = Loadable(
  lazy(() => import("./pages/documentaryValidation/documentExtra"))
);
const VerificationValidationRead = Loadable(
  lazy(() => import("./pages/verificationActionRead/verificationSection"))
);
const DocumentaryValidationRead = Loadable(
  lazy(() => import("./pages/documentaryValidationRead/documentaryValidation"))
);
const DocumentExtraRead = Loadable(
  lazy(() => import("./pages/documentaryValidationRead/documentExtra"))
);
const Form = Loadable(lazy(() => import("./pages/fillForm/form")));

const SubsanationForm = Loadable(
  lazy(() => import("./pages/subsanationForm/subsanationForm"))
);

// const Login = Loadable(lazy(() => import("./pages/authentication/login")));
const Logout = Loadable(lazy(() => import("./pages/authentication/logout")));

const RoutingLogin = Loadable(
  lazy(() => import("./pages/authentication/routing-login"))
);

const RoutingLoginLoad = Loadable(
  lazy(() => import("./pages/authentication/routingLoginLoad"))
);

const FinalizationRequest = Loadable(
  lazy(() => import("./pages/finish/finalizacionRequest"))
);

const RevokeGse = Loadable(lazy(() => import("./pages/revokeGse/RevokeGse")));
const ListRevoke = Loadable(lazy(() => import("./pages/revokeGse/ListRevoke")));
const FinishRevoke = Loadable(
  lazy(() => import("./pages/revokeGse/FinishRevoke"))
);
const InfoCard = Loadable(lazy(() => import("./pages/revokeGse/InfoCard")));

const ForgetPassword = Loadable(
  lazy(() => import("./pages/authentication/forget-password"))
);
const ChangePassword = Loadable(
  lazy(() => import("./pages/authentication/change-password"))
);
const Error = Loadable(lazy(() => import("./pages/404")));
const RecoveryEmailSentPage = Loadable(
  lazy(() => import("./pages/recoveryEmailsent"))
);
const PasswordUpdatedSuccessfullyPage = Loadable(
  lazy(() => import("./pages/authentication/passwordUpdatedSuccessfully"))
);
const PasswordExpiredPage = Loadable(
  lazy(() => import("./pages/authentication/passwordExpired"))
);
const RestoreLinkExpiredPage = Loadable(
  lazy(() => import("./pages/authentication/restoreLinkExpired"))
);

const ListRequestPage = Loadable(
  lazy(() => import("./pages/dashboardsListRequest/MainListRequest"))
);

function ValidateTokenWrapper(Component) {
  return function Wrapper(props) {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      async function checkToken() {
        const isValid = await validateTokenChangePassword({ token: id });
        if (isValid.status === "error") {
          navigate("/restoreLinkExpired");
        }
        setLoading(false);
      }

      checkToken();
    }, [id, navigate]);

    return loading ? null : <Component {...props} />;
  };
}

const ChangePasswordWithValidation = ValidateTokenWrapper(ChangePassword);

const routes = () => {
  return [
    ...authRoutes,
    {
      path: "dashboard",
      element: (
        <ProtectedDashboard>
          <DashboardLayoutV3 />
        </ProtectedDashboard>
      ),
    },
    {
      path: "dashboards",
      element: (
        <ProtectedRoute
          permisionConsult={ConsultPrivileges.proyTconsultPrivileges}
        >
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: dashboardRoutes,
    },
    {
      path: "clientSection",
      element: (
        <ProtectedRoute
          permisionConsult={[
            ConsultPrivileges.suscriberConsultPrivilege,
            ConsultPrivileges.suscriberConsultSupPrivilege
          ]}
        >
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: suscriberRoutes,
    },
    {
      path: "verificationSection",
      element: (
        <ProtectedRoute
          permisionConsult={ConsultPrivileges.checkerConusltPrivilege}
        >
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: checkerRoutes,
    },
    {
      path: "documentaryValidation",
      element: (
        <ProtectedRoute permisionConsult={ConsultPrivileges.managementChecker}>
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: verificationRoutes,
    },
    {
      path: "documentaryValidationRead",
      element: (
        <ProtectedRoute permisionConsult={ConsultPrivileges.documentaryValidationReadOnly}>
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: verificationRoutesReadOnly,
    },
    {
      path: "Myself",
      element: (
        <ProtectedRoute
          permisionConsult={ConsultPrivileges.myselfConusltPrivilege}
        >
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: myselfRoutes,
    },
    {
      path: "Electronics",
      element: (
        <ProtectedRoute permisionConsult={ConsultPrivileges.consultElectronic}>
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: electonicRoutes,
    },
    {
      path: "credentialsVerify",
      element: (
        <ProtectedRoute permisionConsult={ConsultPrivileges.veriCredentialsECD}>
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: credentialsRoutes,
    },
    {
      path: "credentials",
      element: (
        <ProtectedRoute permisionConsult={ConsultPrivileges.credentialsECD}>
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: credentialsRoutes,
    },
    {
      path: "RequestManagement",
      element: (
        <ProtectedRoute
          permisionConsult={ConsultPrivileges.otherProductsConsult}
        >
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: requestManagementRoutes,
    },
    {
      path: "Roles",
      element: (
        <ProtectedRoute permisionConsult={ConsultPrivileges.consultRole}>
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: rolesRoutes,
    },
    {
      path: "Usuarios",
      element: (
        <ProtectedRoute permisionConsult={ConsultPrivileges.consultUser}>
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: userRoutes,
    },
    {
      path: "configurations",
      element: (
        <ProtectedRoute permisionConsult={ConsultPrivileges.consultConfig}>
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: configurationRoutes,
    },
    {
      path: "Ubicaciones",
      element: (
        <ProtectedRoute permisionConsult={ConsultPrivileges.locationList}>
          <DashboardLayoutV3 />
        </ProtectedRoute>
      ),
      children: ubicationRoutes,
    },
    {
      path: "Logistica",
      element: <ProtectedRoute permisionConsult={ConsultPrivileges.logisticRoutes}>
        <DashboardLayoutV3 />
      </ProtectedRoute>,
      children: logisticRoutes,
    },
    {
      path : "convenios",
      element: <ProtectedRoute permisionConsult={ConsultPrivileges.supervisorConsult}>
        <DashboardLayoutV3 />
      </ProtectedRoute>,
      children: agreementsRoutes,
    },
    {
      //Cambiar privilegios 
      path : "revokesupervisor",
      element: <ProtectedRoute permisionConsult={ConsultPrivileges.supervisorConsult}> 
        <DashboardLayoutV3 />
      </ProtectedRoute>,
      children: listRequestRoutes,
    },
    ...revokeRoutes,
    {
      path: "*",
      element: <RedirectComponent />,
    },
  ];
};
const revokeRoutes = [
  {
    path: "revocation",
    element: <RevokeGse />,
  },
  {
    path: "/ListRevoke",
    element: <ListRevoke />,
  },
  {
    path: "/FinishRevoke",
    element: <FinishRevoke />,
  },
  {
    path: "/InfoCard",
    element: <InfoCard />,
  },
];
const authRoutes = [
  {
    path: "/",
    element: <RedirectComponent />,
  },
  {
    path: "login",
    element: <RedirectComponent />,
  },
  {
    path: "routing-login/:id",
    element: <RoutingLogin />,
  },
  {
    path: "routingLoginLoad",
    element: <RoutingLoginLoad />,
  },
  {
    path: "finalization-request/:id",
    element: <FinalizationRequest />,
  },
  {
    path: "logout",
    element: <Logout />,
  },
  {
    path: "forget-password",
    element: <ForgetPassword />,
  },
  {
    path: "change-password/:id",
    element: <ChangePasswordWithValidation />,
  },
  {
    path: "errorLogin",
    element: <Error />,
  },
  {
    path: "recoveryEmailSent",
    element: <RecoveryEmailSentPage />,
  },
  {
    path: "passwordUpdatedSuccessfully",
    element: <PasswordUpdatedSuccessfullyPage />,
  },
  {
    path: "passwordExpired",
    element: <PasswordExpiredPage />,
  },
  {
    path: "restoreLinkExpired",
    element: <RestoreLinkExpiredPage />,
  },
  {
    path: "restoreLinkExpired",
    element: <RestoreLinkExpiredPage />,
  },
];
const dashboardRoutes = [
  {
    path: "",
    element: <MainDashboard />,
  },
];
const suscriberRoutes = [
  {
    path: "",
    element: <ClientSection />,
  },
  {
    path: "form",
    element: <Form />,
  },
  {
    path: "form-correction",
    element: <SubsanationForm />,
  },
];

const verificationRoutes = [
  {
    path: "",
    element: <DocumentaryValidation />,
  },
  {
    path: "document",
    element: <DocumentExtra />,
  },
  {
    path: "verificationValidation",
    element: <VerificationValidation />,
  },
];

const verificationRoutesReadOnly = [
  {
    path: "",
    element: <DocumentaryValidationRead />,
  },
  {
    path: "document",
    element: <DocumentExtraRead />,
  },
  {
    path: "verificationValidation",
    element: <VerificationValidationRead />,
  },
];

const checkerRoutes = [
  {
    path: "",
    element: <VerificationList />,
  },
];
const myselfRoutes = [
  {
    path: "",
    element: (
      <ProtectedMySelf
        dashBoardReview={<MySelfDashboardReview />}
        dashBoardIssuance={<MySelfDashboardIssuance />}
      />
    ),
  },
];
const electonicRoutes = [
  {
    path: "",
    element: <ElectronicList />,
  },
];
const requestManagementRoutes = [
  {
    path: "",
    element: <RequestManagement />,
  },
];
const rolesRoutes = [
  {
    path: "",
    element: <RoleList />,
  },
  {
    path: "Create",
    element: (
      <ProtectedRoute permisionConsult={ConsultPrivileges.createRole}>
        <CreateRoles editRole={false} />
      </ProtectedRoute>
    ),
  },
  {
    path: "Edit",
    element: (
      <ProtectedRoute permisionConsult={ConsultPrivileges.editRole}>
        <EditRoles editRole={true} />
      </ProtectedRoute>
    ),
  },
];
const userRoutes = [
  {
    path: "",
    element: <UserList />,
  },
  {
    path: "Create",
    element: (
      <ProtectedRoute permisionConsult={ConsultPrivileges.createUser}>
        <CreateUsers editUser={false} />
      </ProtectedRoute>
    ),
  },
  {
    path: "Edit",
    element: (
      <ProtectedRoute permisionConsult={ConsultPrivileges.editUser}>
        <EditUsers editUser={true} />
      </ProtectedRoute>
    ),
  },
];
const ubicationRoutes = [
  {
    path: "",
    element: <Ubications />,
  },
];

const configurationRoutes = [
  {
    path: "",
    element: <ConfigurationsList />,
  },
];

const credentialsRoutes = [
  {
    path: "",
    element: <Credentials />,
  },
];
const logisticRoutes = [
  {
    path: "",
    element: <Logistics />,
  },
];
const agreementsRoutes = [
  {
    path: "",
    element: <Agreements />,
  }
];
const listRequestRoutes = [
  {
    path: "",
    element: <ListRequestPage />,
  }
];
export default routes;
